import React from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PlayIcon, VideoCameraIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Small button to fetch and play the interview recording (audio or video).
 * @param {() => Promise<{ url: string }>} [getRecordingUrl] – override API (e.g. public share link proxy).
 */
export default function RecordingPlaybackButton({ sessionId, recordingType, getRecordingUrl }) {
  const [state, setState] = React.useState('idle'); // idle | loading | ready | error
  const [url, setUrl] = React.useState(null);
  const [showPlayer, setShowPlayer] = React.useState(false);

  const fetchUrl = async () => {
    setState('loading');
    try {
      const res = getRecordingUrl
        ? await getRecordingUrl()
        : await axios.get(`/api/ai-interviews/session/${sessionId}/recording-url`);
      setUrl(res.data.url);
      setState('ready');
      setShowPlayer(true);
    } catch (err) {
      if (err.response?.status === 202) {
        toast('Recording is still being processed. Try again shortly.');
        setState('idle');
      } else {
        toast.error('Could not load recording');
        setState('error');
      }
    }
  };

  const isVideo = recordingType === 'video';

  if (showPlayer && url) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => setShowPlayer(false)}
      >
        <div
          className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h4 className="font-semibold text-gray-900 text-sm">
              {isVideo ? 'Video Recording' : 'Audio Recording'}
            </h4>
            <button
              type="button"
              onClick={() => setShowPlayer(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4">
            {isVideo ? (
              <video src={url} controls autoPlay className="w-full rounded-lg" />
            ) : (
              <audio src={url} controls autoPlay className="w-full" />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={fetchUrl}
      disabled={state === 'loading'}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition-colors disabled:opacity-50"
    >
      {state === 'loading' ? (
        <div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
      ) : isVideo ? (
        <VideoCameraIcon className="w-4 h-4" />
      ) : (
        <PlayIcon className="w-4 h-4" />
      )}
      {isVideo ? 'Play Video' : 'Play Audio'}
    </button>
  );
}
